// Setup › Webhooks (`/c/{slug}/setup/webhooks`; M1-07; managers; a core feature). Two tabs
// (`?tab=incoming|outgoing`): Incoming is incoming-webhooks.tsx (WEBHOOKS-IN); Outgoing, the
// company's outbound webhooks: list, "New webhook" / "Edit" (URL, description, the event types it
// receives), enable / disable, delete, a signed test send, secret rotation and the delivery log
// with a manual retry. One call on load (the list, cursor-paged); the delivery log loads when its
// dialog opens.
//
// The signing secret (`whsec_…`) is in the create / rotate response only. It lives in the state
// of the dialog that shows it, which cannot be dismissed until "I've copied it" is ticked, and is
// dropped when the dialog closes; the mutation cache only holds the webhook row.
import type { OperationInput, OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Input,
  Label,
  Notice,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
  ViewTabs,
} from "@companyos/ui";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import {
  CopyIcon,
  HistoryIcon,
  KeyRoundIcon,
  PencilIcon,
  PlusIcon,
  PowerIcon,
  PowerOffIcon,
  SendIcon,
  Trash2Icon,
  WebhookIcon,
} from "lucide-react";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import { EVENT_GROUPS } from "./event-types";
import { IncomingWebhooks } from "./incoming-webhooks";
import { PausedBanner, type Scope, ScopeField, scopeLabel, useContainers } from "./webhook-pause";

type Hook = OperationOutput<"webhooks.list">["data"][number];
type Delivery = OperationOutput<"webhooks.deliveries">["data"][number];
type EventType = OperationInput<"webhooks.create">["body"]["types"][number];
type TestResult = OperationOutput<"webhooks.test">;

const hooksKey = (co: string) => ["company", co, "webhooks"] as const;
const deliveriesKey = (co: string, id: string) => ["company", co, "webhooks", id, "deliveries"];

export const webhooksQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: hooksKey(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.webhooks.list({
        params: { co },
        query: { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

const deliveriesQuery = (api: Api, co: string, id: string) =>
  infiniteQueryOptions({
    queryKey: deliveriesKey(co, id),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.webhooks.deliveries({
        params: { co, id },
        query: { limit: 25, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export type Secret = {
  url: string;
  secret: string;
  created: boolean;
  /** Replaces the outgoing hook's description (an incoming hook's senders sign with it). */
  note?: string;
};

type Tab = "incoming" | "outgoing";
const TABS: { value: Tab; label: string }[] = [
  { value: "incoming", label: "Incoming" },
  { value: "outgoing", label: "Outgoing" },
];

/** Setup › Webhooks: two tabs, Incoming (senders → tasks for a DE) and Outgoing (events → URLs). */
export function WebhooksScreen({
  api,
  company,
  frame = "setup",
}: {
  api: Api;
  company: CompanyAccess;
  /** Where it is mounted: Setup › Webhooks, or the Agents mode's Webhooks (crumb "Agents"). */
  frame?: "setup" | "agents";
}) {
  // The tab is `?tab=incoming|outgoing`, read raw (the route declares no search schema).
  // Outgoing by default: the page's existing links and specs land where they always did.
  const tab: Tab =
    (useLocation().search as { tab?: unknown }).tab === "incoming" ? "incoming" : "outgoing";
  const navigate = useNavigate();
  return (
    <SetupPage
      {...(frame === "agents" ? { crumbs: ["Agents"], slot: "webhooks" } : {})}
      title="Webhooks"
      toolbar={
        <div className="px-6 max-md:px-4">
          <ViewTabs
            className="min-w-0"
            aria-label="Webhooks"
            value={tab}
            onValueChange={(v) =>
              void navigate({
                to: ".",
                search: { tab: v === "outgoing" ? "outgoing" : "incoming" } as never,
                replace: true,
              })
            }
            tabs={TABS}
          />
        </div>
      }
    >
      {tab === "incoming" ? (
        <IncomingWebhooks api={api} company={company} />
      ) : (
        <OutgoingWebhooks api={api} company={company} />
      )}
    </SetupPage>
  );
}

/** The Outgoing tab: the company's outbound webhooks. */
function OutgoingWebhooks({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useInfiniteQuery(webhooksQuery(api, company.id));
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Hook | "new" | null>(null);
  const [secret, setSecret] = useState<Secret | null>(null);
  const [deliveriesOf, setDeliveriesOf] = useState<Hook | null>(null);
  const [rotating, setRotating] = useState<Hook | null>(null);
  const [deleting, setDeleting] = useState<Hook | null>(null);
  const [tests, setTests] = useState<Record<string, TestResult & { url: string }>>({});
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const refresh = () => void qc.invalidateQueries({ queryKey: hooksKey(company.id) });
  // WOS-BUNDLE: each hook's scope reads as the space or board it names.
  const containers = useContainers(api, company.id);

  const toggle = useMutation({
    mutationFn: (h: Hook) =>
      api.client.webhooks.update({
        params: { co: company.id, id: h.id },
        body: { status: h.status === "active" ? "disabled" : "active" },
      }),
    onSuccess: (h) => {
      refresh();
      toast.add({
        title: h.status === "active" ? "Webhook enabled" : "Webhook disabled",
        type: "success",
      });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The webhook was not changed."), type: "error" }),
  });
  const test = useMutation({
    mutationFn: async (h: Hook) => ({
      ...(await api.client.webhooks.test({ params: { co: company.id, id: h.id } })),
      url: h.url,
      id: h.id,
    }),
    onSuccess: ({ id, ...r }) => {
      setTests((t) => ({ ...t, [id]: r }));
      void qc.invalidateQueries({ queryKey: deliveriesKey(company.id, id) });
    },
    onError: (e) => toast.add({ title: problemText(e, "The test was not sent."), type: "error" }),
  });

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setEditing("new")}>
          <PlusIcon data-icon="inline-start" />
          New webhook
        </Button>
      </div>
      {q.isPending ? (
        <Loading label="Loading webhooks" rows={3} />
      ) : q.isError ? (
        <LoadError what="Webhooks" error={q.error} />
      ) : !rows.length ? (
        <EmptyState
          variant="card"
          icon={WebhookIcon}
          title="No webhooks yet"
          description="Post the events you choose to a URL, signed."
          actions={
            <Button size="sm" onClick={() => setEditing("new")}>
              New webhook
            </Button>
          }
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          {/* WOS-BUNDLE: a hook paused after five events in a row failed every retry. */}
          <PausedBanner api={api} company={company} hooks={rows} onResumed={refresh} />
          <Table data-slot="setup-webhooks" className={DENSE_TABLE}>
            <TableCaption className="sr-only">Outbound webhooks of the company.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">URL</TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  Events
                </TableHead>
                <TableHead scope="col">Status</TableHead>
                <TableHead scope="col" className="max-lg:hidden">
                  Created
                </TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((h) => (
                <TableRow key={h.id} data-webhook={h.id} className="group/row">
                  <TableHead scope="row" className="min-w-0 font-medium">
                    <span
                      className="block max-w-[32ch] truncate font-mono text-xs max-md:max-w-[18ch]"
                      title={h.url}
                    >
                      {h.url}
                    </span>
                    {h.description ? (
                      <span className="block max-w-[40ch] truncate text-xs font-normal text-muted-foreground max-md:hidden">
                        {h.description}
                      </span>
                    ) : null}
                    <span
                      data-slot="webhook-scope"
                      className="block text-xs font-normal text-muted-foreground"
                    >
                      {scopeLabel(h.filter, containers.nameOf)}
                    </span>
                  </TableHead>
                  <TableCell className="max-md:hidden">
                    <span title={h.types.join(", ")}>
                      {h.types.length} {h.types.length === 1 ? "event type" : "event types"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        h.status === "active"
                          ? "success"
                          : h.status === "paused"
                            ? "warning"
                            : "danger"
                      }
                      dot
                    >
                      {h.status === "active"
                        ? "Active"
                        : h.status === "paused"
                          ? "Paused"
                          : "Disabled"}
                    </Badge>
                    {h.consecutive_failures > 0 ? (
                      <span
                        data-slot="webhook-failing"
                        className="block text-xs text-warning-text tabular-nums"
                      >
                        {h.consecutive_failures} failing in a row
                      </span>
                    ) : null}
                    {h.status === "disabled" && h.disabled_reason ? (
                      <span className="block text-xs text-muted-foreground">
                        {h.disabled_reason}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="max-lg:hidden text-muted-foreground">
                    {formatDateTime(h.created_at)}
                  </TableCell>
                  <TableCell className="w-px">
                    <RowActions>
                      <RowAction
                        icon={PencilIcon}
                        label={`Edit ${h.url}`}
                        tip="Edit"
                        onClick={() => setEditing(h)}
                      />
                      <RowAction
                        icon={SendIcon}
                        label={`Send test to ${h.url}`}
                        tip="Send test"
                        disabled={test.isPending}
                        onClick={() => test.mutate(h)}
                      />
                      <RowAction
                        icon={HistoryIcon}
                        label={`Deliveries of ${h.url}`}
                        tip="Deliveries"
                        onClick={() => setDeliveriesOf(h)}
                      />
                      <RowAction
                        icon={KeyRoundIcon}
                        label={`Rotate secret of ${h.url}`}
                        tip="Rotate secret"
                        onClick={() => setRotating(h)}
                      />
                      <RowAction
                        icon={h.status === "active" ? PowerOffIcon : PowerIcon}
                        label={`${h.status === "active" ? "Disable" : "Enable"} ${h.url}`}
                        tip={h.status === "active" ? "Disable" : "Enable"}
                        disabled={toggle.isPending}
                        onClick={() => toggle.mutate(h)}
                      />
                      <RowAction
                        icon={Trash2Icon}
                        label={`Delete ${h.url}`}
                        tip="Delete"
                        className="hover:text-danger-text"
                        onClick={() => setDeleting(h)}
                      />
                    </RowActions>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <LoadMore
            what="webhooks"
            hasMore={q.hasNextPage}
            loading={q.isFetchingNextPage}
            onMore={() => void q.fetchNextPage()}
          />
          {Object.entries(tests).map(([id, r]) => (
            <Notice key={id} tone={r.ok ? "success" : "danger"} role="status" data-test-result={id}>
              Test to {r.url}:{" "}
              {r.ok
                ? `delivered (HTTP ${r.response_status})`
                : `failed (${r.error ?? "no answer"})`}
              .
            </Notice>
          ))}
        </div>
      )}
      {editing ? (
        <WebhookDialog
          key={editing === "new" ? "new" : editing.id}
          api={api}
          company={company}
          hook={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSecret={setSecret}
        />
      ) : null}
      {secret ? <SecretDialog secret={secret} onClose={() => setSecret(null)} /> : null}
      {rotating ? (
        <RotateDialog
          api={api}
          company={company}
          hook={rotating}
          onClose={() => setRotating(null)}
          onSecret={setSecret}
        />
      ) : null}
      {deleting ? (
        <DeleteDialog
          api={api}
          company={company}
          hook={deleting}
          onClose={() => setDeleting(null)}
          onDone={refresh}
        />
      ) : null}
      {deliveriesOf ? (
        <DeliveriesDialog
          api={api}
          company={company}
          hook={deliveriesOf}
          onClose={() => setDeliveriesOf(null)}
        />
      ) : null}
    </div>
  );
}

function WebhookDialog({
  api,
  company,
  hook,
  onClose,
  onSecret,
}: {
  api: Api;
  company: CompanyAccess;
  hook: Hook | null;
  onClose: () => void;
  onSecret: (s: Secret) => void;
}) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(hook?.url ?? "");
  const [description, setDescription] = useState(hook?.description ?? "");
  const [types, setTypes] = useState<string[]>(hook?.types ?? []);
  const [scope, setScope] = useState<Scope>(hook?.filter.container ?? null);
  const [find, setFind] = useState("");
  const [touched, setTouched] = useState(false);
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        url: url.trim(),
        description: description.trim() || null,
        // The picker offers catalog types only (webhooks.test.tsx keeps the list in step).
        types: types as EventType[],
        // WOS-BUNDLE: the whole company, or one space or board (other filter keys kept).
        filter: (() => {
          const { container: _c, ...rest } = hook?.filter ?? {};
          return scope ? { ...rest, container: scope } : rest;
        })(),
      };
      if (hook) {
        await api.client.webhooks.update({ params: { co: company.id, id: hook.id }, body });
        return null;
      }
      const created = await api.client.webhooks.create({ params: { co: company.id }, body });
      return { url: created.webhook.url, secret: created.secret, created: true };
    },
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: hooksKey(company.id) });
      onClose();
      if (created) onSecret(created);
      else toast.add({ title: "Webhook saved", type: "success" });
    },
  });
  const urlBad = !/^https?:\/\/\S+$/i.test(url.trim());
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!urlBad && types.length) save.mutate();
  };
  const needle = find.trim().toLowerCase();
  const toggleType = (t: string, on: boolean) =>
    setTypes((prev) => (on ? [...new Set([...prev, t])] : prev.filter((x) => x !== t)));
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{hook ? "Edit webhook" : "New webhook"}</DialogTitle>
            <DialogDescription>
              Each event you pick is sent as a signed POST to the URL. Use an https address that is
              reachable from the internet.
            </DialogDescription>
          </DialogHeader>
          <Field data-invalid={(touched && urlBad) || undefined}>
            <FieldLabel htmlFor="webhook-url">URL</FieldLabel>
            <Input
              id="webhook-url"
              className="h-10 font-mono text-xs"
              inputMode="url"
              placeholder="https://hooks.example.com/companyos"
              value={url}
              aria-invalid={(touched && urlBad) || undefined}
              onChange={(e) => setUrl(e.target.value)}
            />
            {touched && urlBad ? (
              <p role="alert" className="text-xs text-danger-text">
                Enter a full URL, starting with https://.
              </p>
            ) : null}
          </Field>
          <Field>
            <FieldLabel htmlFor="webhook-description">Description</FieldLabel>
            <Textarea
              id="webhook-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <ScopeField api={api} co={company.id} value={scope} onChange={setScope} />
          <FieldSet data-invalid={(touched && !types.length) || undefined}>
            <FieldLegend variant="label">Events ({types.length} selected)</FieldLegend>
            <Input
              aria-label="Find an event type"
              className="h-9"
              placeholder="Find an event type"
              value={find}
              onChange={(e) => setFind(e.target.value)}
            />
            <div
              data-slot="webhook-events"
              className="flex max-h-64 min-w-0 flex-col gap-3 overflow-y-auto rounded-md border border-border p-3"
            >
              {EVENT_GROUPS.map((g) => {
                const shown = g.types.filter((t) => !needle || t.includes(needle));
                if (!shown.length) return null;
                const all = shown.every((t) => types.includes(t));
                return (
                  <fieldset key={g.key} className="flex min-w-0 flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <legend className="text-xs font-semibold text-secondary">{g.label}</legend>
                      <Button
                        type="button"
                        size="xs"
                        variant="ghost"
                        aria-label={`${all ? "Clear" : "Select all"} ${g.label} events`}
                        onClick={() =>
                          setTypes((prev) =>
                            all
                              ? prev.filter((t) => !shown.includes(t))
                              : [...new Set([...prev, ...shown])],
                          )
                        }
                      >
                        {all ? "Clear" : "Select all"}
                      </Button>
                    </div>
                    {shown.map((t) => (
                      <TypeBox
                        key={t}
                        type={t}
                        checked={types.includes(t)}
                        onChange={(on) => toggleType(t, on)}
                      />
                    ))}
                  </fieldset>
                );
              })}
            </div>
            {touched && !types.length ? (
              <p role="alert" className="text-xs text-danger-text">
                Pick at least one event type.
              </p>
            ) : null}
          </FieldSet>
          {save.isError ? (
            <Notice tone="danger" role="alert">
              {problemText(save.error, "The webhook was not saved.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" />} disabled={save.isPending}>
              Cancel
            </DialogClose>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : hook ? "Save" : "Create webhook"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TypeBox({
  type,
  checked,
  onChange,
}: {
  type: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="font-mono text-xs">
        {type}
      </Label>
    </div>
  );
}

/** The one-time secret. Cannot be dismissed until "I've copied it" is ticked. */
export function SecretDialog({ secret, onClose }: { secret: Secret; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "done" | "failed">("idle");
  const secretId = useId();
  const confirmId = useId();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret.secret);
      setCopyState("done");
    } catch {
      setCopyState("failed");
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && copied && onClose()}>
      <DialogContent showCloseButton={false} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Copy the signing secret</DialogTitle>
          <DialogDescription>
            {secret.note ?? (
              <>
                This is the only time the secret is shown. Use it to verify the{" "}
                <code>X-CompanyOS-Signature</code> header of {secret.created ? "every" : "each"}{" "}
                delivery to {secret.url}; CompanyOS cannot show it again, only replace it.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={secretId}>Signing secret</FieldLabel>
          <div className="flex min-w-0 gap-2">
            <Input
              id={secretId}
              readOnly
              value={secret.secret}
              className="min-w-0 font-mono text-xs"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button variant="outline" onClick={() => void copy()} className="max-md:h-11">
              <CopyIcon aria-hidden="true" />
              Copy
            </Button>
          </div>
          <FieldDescription aria-live="polite">
            {copyState === "done"
              ? "Copied to the clipboard."
              : copyState === "failed"
                ? "Copy failed: select the secret and copy it by hand."
                : "Store it with your receiver's secrets."}
          </FieldDescription>
        </Field>
        <div className="flex items-center gap-2">
          <Checkbox
            id={confirmId}
            checked={copied}
            onCheckedChange={(v) => setCopied(v === true)}
          />
          <Label htmlFor={confirmId}>I've copied it</Label>
        </div>
        <DialogFooter>
          <Button disabled={!copied} onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RotateDialog({
  api,
  company,
  hook,
  onClose,
  onSecret,
}: {
  api: Api;
  company: CompanyAccess;
  hook: Hook;
  onClose: () => void;
  onSecret: (s: Secret) => void;
}) {
  const session = useRef(0);
  useEffect(
    () => () => {
      session.current += 1;
    },
    [],
  );
  const rotate = useMutation({
    mutationFn: async () => {
      const mine = session.current;
      const r = await api.client.webhooks.rotateSecret({
        params: { co: company.id, id: hook.id },
      });
      return mine === session.current ? { url: hook.url, secret: r.secret, created: false } : null;
    },
    onSuccess: (s) => {
      onClose();
      if (s) onSecret(s);
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !rotate.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rotate the signing secret?</DialogTitle>
          <DialogDescription>
            Deliveries to {hook.url} are signed with a new secret from now on. The old one stops
            verifying at once, so update the receiver right after.
          </DialogDescription>
        </DialogHeader>
        {rotate.isError ? (
          <Notice tone="danger" role="alert">
            {problemText(rotate.error, "The secret was not rotated.")}
          </Notice>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />} disabled={rotate.isPending}>
            Cancel
          </DialogClose>
          <Button disabled={rotate.isPending} onClick={() => rotate.mutate()}>
            {rotate.isPending ? "Rotating…" : "Rotate secret"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  api,
  company,
  hook,
  onClose,
  onDone,
}: {
  api: Api;
  company: CompanyAccess;
  hook: Hook;
  onClose: () => void;
  onDone: () => void;
}) {
  const del = useMutation({
    mutationFn: () => api.client.webhooks.delete({ params: { co: company.id, id: hook.id } }),
    onSuccess: () => {
      onDone();
      toast.add({ title: "Webhook deleted", type: "success" });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !del.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this webhook?</DialogTitle>
          <DialogDescription>
            {hook.url} stops receiving events and its delivery log is removed. This cannot be
            undone.
          </DialogDescription>
        </DialogHeader>
        {del.isError ? (
          <Notice tone="danger" role="alert">
            {problemText(del.error, "The webhook was not deleted.")}
          </Notice>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />} disabled={del.isPending}>
            Cancel
          </DialogClose>
          <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>
            {del.isPending ? "Deleting…" : "Delete webhook"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_BADGE: Record<Delivery["status"], "success" | "warning" | "danger" | "neutral"> = {
  succeeded: "success",
  pending: "warning",
  failed: "danger",
  abandoned: "danger",
};

function DeliveriesDialog({
  api,
  company,
  hook,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  hook: Hook;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const q = useInfiniteQuery(deliveriesQuery(api, company.id, hook.id));
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const retry = useMutation({
    mutationFn: (d: Delivery) =>
      api.client.webhooks.retryDelivery({
        params: { co: company.id, id: hook.id, delivery_id: d.id },
      }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: deliveriesKey(company.id, hook.id) });
      toast.add({
        title: d.status === "succeeded" ? "Delivery sent" : "Delivery failed again",
        type: d.status === "succeeded" ? "success" : "error",
      });
    },
    onError: (e) => toast.add({ title: problemText(e, "The retry was not sent."), type: "error" }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Deliveries</DialogTitle>
          <DialogDescription className="break-all font-mono text-xs">{hook.url}</DialogDescription>
        </DialogHeader>
        {q.isPending ? (
          <Loading label="Loading deliveries" rows={3} />
        ) : q.isError ? (
          <LoadError what="Deliveries" error={q.error} />
        ) : !rows.length ? (
          <EmptyState
            variant="card"
            icon={WebhookIcon}
            title="No deliveries yet"
            description="Send a test, or wait for one of the subscribed events."
          />
        ) : (
          <div className="flex max-h-[60vh] min-w-0 flex-col gap-3 overflow-auto">
            <Table data-slot="webhook-deliveries" className={DENSE_TABLE}>
              <TableCaption className="sr-only">Deliveries, newest first.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Event</TableHead>
                  <TableHead scope="col">Status</TableHead>
                  <TableHead scope="col">Attempts</TableHead>
                  <TableHead scope="col">Response</TableHead>
                  <TableHead scope="col">Sent</TableHead>
                  <TableHead scope="col">Next attempt</TableHead>
                  <TableHead scope="col">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((d) => (
                  <TableRow key={d.id} data-delivery={d.id}>
                    <TableHead scope="row" className="font-mono text-xs font-normal">
                      {d.event_type}
                    </TableHead>
                    <TableCell>
                      <Badge variant={STATUS_BADGE[d.status]}>{d.status}</Badge>
                    </TableCell>
                    <TableCell>{d.attempts}</TableCell>
                    <TableCell className="text-xs">
                      {d.response_status ?? "—"}
                      {d.error ? (
                        <span className="block text-muted-foreground">{d.error}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateTime(d.delivered_at ?? d.created_at)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {d.next_attempt_at ? formatDateTime(d.next_attempt_at) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {d.status === "failed" || d.status === "abandoned" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          aria-label={`Retry ${d.event_type} delivery`}
                          disabled={retry.isPending}
                          onClick={() => retry.mutate(d)}
                        >
                          Retry
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <LoadMore
              what="deliveries"
              hasMore={q.hasNextPage}
              loading={q.isFetchingNextPage}
              onMore={() => void q.fetchNextPage()}
            />
          </div>
        )}
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />}>Close</DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
